import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, Trash2 } from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import FloatingActions from '../components/FloatingActions';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-2xl sm:text-3xl text-gold mb-3">{title}</h2>
    <div className="text-text-main text-[15px] leading-[1.9] space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <SEO
        title="Privacy Policy | ViratAstro"
        description="ViratAstro की Privacy Policy — आपका data कैसे sुरक्षित रखा जाता है, क्या-क्या collect होता है, और आपके अधिकार। DPDP Act 2023, GDPR, UAE Data Law compliant।"
        path="/privacy-policy"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'Privacy Policy', path: '/privacy-policy' },
        ]}
      />
      <Header />
      <section className="pt-28 pb-16">
        <div className="container-custom max-w-3xl">
          <Link to="/" className="text-sm text-text-muted hover:text-gold mb-4 inline-block">← होम</Link>

          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-8 h-8 text-gold" />
            <h1 className="font-heading text-4xl sm:text-5xl text-text-main">Privacy Policy</h1>
          </div>
          <p className="text-text-muted text-sm mb-2">गोपनीयता नीति · आपकी जानकारी की सुरक्षा हमारे लिए सर्वोपरि है।</p>
          <p className="text-text-muted/70 text-xs mb-8">Last updated: 13 May 2026</p>

          <Section title="1. हम क्या जानकारी एकत्र करते हैं">
            <p>ज्योतिषीय विश्लेषण हेतु हम निम्नलिखित जानकारी एकत्र करते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-gold">जन्म विवरण:</strong> नाम, जन्म तिथि, जन्म समय, जन्म स्थान, लिंग</li>
              <li><strong className="text-gold">संपर्क:</strong> WhatsApp number या email (केवल service delivery हेतु)</li>
              <li><strong className="text-gold">आपके प्रश्न:</strong> जो आप पूछते हैं</li>
              <li><strong className="text-gold">तकनीकी data:</strong> IP address, browser, device (analytics हेतु, anonymous)</li>
            </ul>
            <p>हम <strong>payment card details कभी store नहीं करते</strong> — यह सीधे Razorpay (PCI-DSS Level 1 certified) पर handle होता है।</p>
          </Section>

          <Section title="2. आपकी जानकारी कैसे सुरक्षित है">
            <ul className="list-disc pl-6 space-y-1">
              <li>🔐 <strong>Encryption at rest:</strong> सभी sensitive birth details encrypted DB में store</li>
              <li>🛡️ <strong>HTTPS/TLS 1.3:</strong> Data transmission बिल्कुल secure</li>
              <li>🔑 <strong>Access control:</strong> केवल authorized personnel की पहुँच</li>
              <li>📿 <strong>Astrologer privacy:</strong> आचार्य Vivek जी आपकी जानकारी third-party से कभी share नहीं करते</li>
            </ul>
          </Section>

          <Section title="3. आपके अधिकार (DPDP Act 2023 + GDPR)">
            <p>आप किसी भी समय निम्न अधिकारों का प्रयोग कर सकते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-gold">Access:</strong> अपना सारा data देखें</li>
              <li><strong className="text-gold">Correction:</strong> गलत जानकारी सही करवाएँ</li>
              <li><strong className="text-gold">Deletion (भूले जाने का अधिकार):</strong> सारा data permanent delete</li>
              <li><strong className="text-gold">Export:</strong> Data का ZIP download</li>
              <li><strong className="text-gold">Withdraw consent:</strong> कभी भी सहमति वापस</li>
            </ul>
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-4">
              <p className="flex items-center gap-2 text-gold font-semibold mb-2">
                <Trash2 className="w-4 h-4" /> Data Delete request
              </p>
              <p className="text-sm">
                <strong>privacy@viratastro.cloud</strong> पर email करें "Subject: Data Deletion Request" के साथ। हम 30 दिनों में आपका सारा data permanent delete कर देंगे।
              </p>
            </div>
          </Section>

          <Section title="4. Cookies और Analytics">
            <p>हम केवल आवश्यक cookies use करते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Session cookie (login state)</li>
              <li>Language preference</li>
              <li>Google Analytics (anonymized, IP masking enabled)</li>
            </ul>
            <p>कोई tracking cookie नहीं, कोई advertising profile build नहीं होता।</p>
          </Section>

          <Section title="5. Data Retention">
            <p>आपके प्रश्नों का record <strong>1 वर्ष</strong> तक रहता है — आपके dashboard में access के लिए। 1 वर्ष बाद auto-archive (anonymized) हो जाता है।</p>
            <p>आप कभी भी पहले delete करवा सकते हैं।</p>
          </Section>

          <Section title="6. International Transfer (UAE, USA, UK, EU users)">
            <p>हमारे servers <strong>India में</strong> hosted हैं। जब आप विदेश से access करते हैं, आपका data India आता है। यह transfer DPDP Act 2023 के अंतर्गत lawful है।</p>
            <p><strong>EU/UK users:</strong> GDPR के तहत आपके अतिरिक्त अधिकार सुरक्षित हैं। DPO से संपर्क हेतु privacy@viratastro.cloud पर लिखें।</p>
          </Section>

          <Section title="7. नाबालिगों की जानकारी">
            <p>हमारी सेवाएँ <strong>18 वर्ष से ऊपर</strong> के लोगों के लिए हैं। नाबालिग की कुंडली विश्लेषण माता-पिता की written consent से ही होगा।</p>
          </Section>

          <Section title="8. Policy में बदलाव">
            <p>यदि Privacy Policy में कोई बदलाव होगा, हम <strong>email/WhatsApp पर सूचना देंगे</strong> और homepage पर banner लगाएँगे। पुरानी policies पर continued use बदलाव की स्वीकृति मानी जाएगी।</p>
          </Section>

          <Section title="9. हमसे संपर्क">
            <div className="bg-white/5 border border-gold/20 rounded-xl p-5">
              <p className="flex items-center gap-2 text-gold font-semibold mb-2">
                <Mail className="w-4 h-4" /> Data Protection Officer (DPO)
              </p>
              <p><strong>आचार्य Vivek Mudgal</strong></p>
              <p>Email: <span className="text-gold">privacy@viratastro.cloud</span></p>
              <p>WhatsApp: +91-9311369729</p>
              <p>Address: PB-77 GF Parklands Pride, Faridabad, Haryana 121004, India</p>
            </div>
          </Section>

          <div className="text-center text-text-muted text-xs italic mt-12">
            "गोपनीयता एक मौलिक अधिकार है — हम इसे शास्त्र की तरह पवित्र मानते हैं।"
          </div>
        </div>
      </section>
      <FloatingActions />
      <Footer />
    </div>
  );
}
